import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { TCard, TEmpty, TSkeleton } from './table';

/* Shared list helpers: load once, loading / failed / empty states, detail rows, toast. */

/** `load` may return null to wait (e.g. no org yet); `deps` reload it when they change. */
export function useList<T = any>(load: () => Promise<T[]> | null, deps: unknown[] = []) {
    const [rows, setRows] = useState<T[] | null>(null);
    const [failed, setFailed] = useState(false);
    const reload = useCallback(() => {
        const pending = load();
        if (!pending) return;
        pending
            .then((list) => {
                setRows(Array.isArray(list) ? list : []);
                setFailed(false);
            })
            .catch(() => setFailed(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    useEffect(reload, [reload]);
    return { rows, setRows, failed, reload };
}

export function ListState({
    rows,
    failed,
    what,
    reload,
    empty,
    emptyAction,
    onEmptyAction,
    children,
}: {
    rows: unknown[] | null;
    failed: boolean;
    what: string;
    reload: () => void;
    /** message when the (filtered) list is empty */
    empty?: string;
    emptyAction?: string;
    onEmptyAction?: () => void;
    children: React.ReactNode;
}) {
    if (failed)
        return (
            <TCard>
                <TEmpty message={`Couldn’t load ${what}.`} actionLabel="Retry" onAction={reload} />
            </TCard>
        );
    if (rows === null)
        return (
            <TCard>
                <TSkeleton rows={4} />
            </TCard>
        );
    if (empty)
        return (
            <TCard>
                <TEmpty message={empty} actionLabel={emptyAction} onAction={onEmptyAction} />
            </TCard>
        );
    return <>{children}</>;
}

export function Frow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="frow">
            <span className="fl">{label}</span>
            <span className="fv">{children}</span>
        </div>
    );
}

export function useToast() {
    const [toast, setToast] = useState<{ text: string; undo?: () => void } | null>(null);
    useEffect(() => {
        if (!toast) return;
        const t = window.setTimeout(() => setToast(null), 5000);
        return () => window.clearTimeout(t);
    }, [toast]);
    const node = toast ? (
        <div className="toasthost" role="status" aria-live="polite">
            <div className="toast">
                <span className="t1" style={{ flex: 1, minWidth: 0 }}>{toast.text}</span>
                {toast.undo && (
                    <button type="button" className="tb-btn" onClick={() => { toast.undo?.(); setToast(null); }}>
                        Undo
                    </button>
                )}
            </div>
        </div>
    ) : null;
    return { show: (text: string, undo?: () => void) => setToast({ text, undo }), node };
}
